import { useId, useState } from 'react';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Modal } from '@ds/feedback';
import { FormField, Input, Select, Switch } from '@ds/forms';
import { percentualDoTexto } from '@/comuns/dinheiro';
import { moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { TIPOS_DE_METODO } from '@/data/financeiroAvulso';
import type { CadastrosFinanceiros, MetodoNoCadastro, TipoDeMetodo } from '@/data/financeiroAvulso';
import type { Movimentacao } from '@/data/tipos';

function taxaEscrita(taxa: number): string {
    return taxa > 0 ? `${taxa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` : 'sem taxa';
}

export interface MetodosDePagamentoProps {
    cadastros: CadastrosFinanceiros;
    /** Movimentações do mês: é delas que sai o custo de taxa. */
    movimentacoes: Movimentacao[];
    onMudou: () => void;
}

/**
 * Formas de pagamento — taxa e prazo entram no líquido e no dia em que o
 * dinheiro cai na conta. Forma não se apaga, se desativa: os pagamentos antigos
 * apontam para ela.
 */
export function MetodosDePagamento({ cadastros, movimentacoes, onMudou }: MetodosDePagamentoProps) {
    const [alvo, setAlvo] = useState<MetodoNoCadastro | 'novo' | null>(null);

    const ativos = cadastros.metodos.filter((m) => m.ativo);
    const cartoes = ativos.filter((m) => m.tipo === 'credito' || m.tipo === 'debito');
    const taxaMedia = cartoes.length ? cartoes.reduce((s, m) => s + m.taxaPercentual, 0) / cartoes.length : undefined;
    const comTaxa = movimentacoes.filter((m) => m.taxaCentavos > 0);
    const custo = comTaxa.reduce((s, m) => s + m.taxaCentavos, 0);
    const prazoMedio = ativos.length ? Math.round(ativos.reduce((s, m) => s + m.prazoDias, 0) / ativos.length) : undefined;
    const nomeDaConta = (id?: string) => cadastros.contas.find((c) => c.id === id)?.nome ?? '—';
    const rotuloDoTipo = (t: TipoDeMetodo) => TIPOS_DE_METODO.find((x) => x.valor === t)?.rotulo ?? t;

    const colunas: DataTableColumn<MetodoNoCadastro>[] = [
        { key: 'nome', header: 'Forma', sortable: true, render: (m) => <strong style={{ fontWeight: 600 }}>{m.nome}</strong> },
        { key: 'tipo', header: 'Tipo', render: (m) => <Badge tone="neutral">{rotuloDoTipo(m.tipo)}</Badge> },
        { key: 'taxaPercentual', header: 'Taxa', align: 'right', render: (m) => taxaEscrita(m.taxaPercentual) },
        { key: 'prazoDias', header: 'Repasse', render: (m) => (m.prazoDias > 0 ? `D+${m.prazoDias}` : 'na hora') },
        { key: 'contaId', header: 'Conta de destino', render: (m) => nomeDaConta(m.contaId) },
        { key: 'ativo', header: 'Situação', render: (m) => <Badge tone={m.ativo ? 'success' : 'neutral'} dot>{m.ativo ? 'Ativa' : 'Inativa'}</Badge> },
        { key: 'acoes', header: '', align: 'right', width: 100, render: (m) => <span data-parar-linha><Button size="sm" variant="secondary" onClick={() => setAlvo(m)}>Editar</Button></span> },
    ];

    return (
        <>
            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                <KpiCard label="Formas ativas" value={String(ativos.length)} hint={`de ${cadastros.metodos.length} cadastradas`} icon={<Icon name="credit-card" size={16} />} />
                <KpiCard label="Taxa média de cartão" value={taxaMedia === undefined ? '—' : taxaEscrita(taxaMedia)} hint={cartoes.length ? `${cartoes.length} formas de cartão` : 'nenhum cartão ativo'} />
                <KpiCard label="Custo de taxas no mês" value={moeda(custo)} hint={comTaxa.length ? `sobre ${moeda(comTaxa.reduce((s, m) => s + m.valorCentavos, 0))}` : 'nenhum pagamento com taxa'} />
                <KpiCard label="Prazo médio de repasse" value={prazoMedio === undefined ? '—' : `${prazoMedio} dias`} />
            </div>

            <Card
                title="Formas de pagamento" subtitle="Taxa e prazo entram no líquido e no saldo" padding={0}
                actions={<Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setAlvo('novo')}>Nova forma de pagamento</Button>}
            >
                <DataTable
                    columns={colunas} rows={cadastros.metodos} rowKey="id" stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    rowLabel={(m) => m.nome} emptyMessage="Nenhuma forma de pagamento cadastrada."
                />
            </Card>

            {alvo && <MetodoModal alvo={alvo} cadastros={cadastros} onFechar={() => setAlvo(null)} onSalvo={onMudou} />}
        </>
    );
}

interface MetodoModalProps {
    alvo: MetodoNoCadastro | 'novo';
    cadastros: CadastrosFinanceiros;
    onFechar: () => void;
    onSalvo: () => void;
}

function MetodoModal({ alvo, cadastros, onFechar, onSalvo }: MetodoModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idTipo = useId();
    const idTaxa = useId();
    const idPrazo = useId();
    const idConta = useId();
    const novo = alvo === 'novo';

    const inicial = {
        nome: novo ? '' : alvo.nome,
        tipo: (novo ? 'pix' : alvo.tipo) as TipoDeMetodo,
        taxa: novo ? '0' : String(alvo.taxaPercentual).replace('.', ','),
        prazo: novo ? '0' : String(alvo.prazoDias),
        contaId: novo ? '' : alvo.contaId ?? '',
        ativo: novo ? true : alvo.ativo,
    };
    const [campos, setCampos] = useState(inicial);
    const [salvando, setSalvando] = useState(false);
    const mudar = <C extends keyof typeof campos>(campo: C, valor: (typeof campos)[C]) => setCampos((c) => ({ ...c, [campo]: valor }));

    const taxa = percentualDoTexto(campos.taxa);
    const prazo = /^\d+$/.test(campos.prazo.trim()) ? Number(campos.prazo) : null;
    const pode = Boolean(campos.nome.trim()) && taxa !== null && taxa < 100 && prazo !== null;
    const contas = cadastros.contas.filter((c) => c.ativa || c.id === campos.contaId);

    const salvar = async () => {
        if (!pode || taxa === null || prazo === null) return;
        setSalvando(true);
        try {
            await dados().salvarMetodo(novo ? null : alvo.id, {
                nome: campos.nome, tipo: campos.tipo, taxaPercentual: taxa, prazoDias: prazo, ativo: campos.ativo,
                ...(campos.contaId ? { contaId: campos.contaId } : {}),
            });
            aviso({ title: novo ? 'Forma de pagamento cadastrada' : 'Forma de pagamento atualizada', description: campos.nome.trim() });
            onSalvo();
            onFechar();
        } catch (e) {
            aviso({ title: 'Não foi possível salvar a forma de pagamento', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open width={520} onClose={onFechar}
            title={novo ? 'Nova forma de pagamento' : 'Editar forma de pagamento'}
            description="Taxa e prazo alimentam o líquido de cada baixa e o fluxo de caixa."
            dirty={JSON.stringify(campos) !== JSON.stringify(inicial)} onSave={() => { void salvar(); }}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void salvar(); }} disabled={!pode || salvando}>{salvando ? 'Salvando…' : novo ? 'Cadastrar' : 'Salvar'}</Button>
                </>
            )}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14 }}>
                <FormField label="Nome" htmlFor={idNome} required style={{ gridColumn: '1 / -1' }}>
                    <Input id={idNome} value={campos.nome} onChange={(v) => mudar('nome', v)} placeholder="Ex.: Cartão de crédito 6x" />
                </FormField>
                <FormField label="Tipo" htmlFor={idTipo}>
                    <Select id={idTipo} value={campos.tipo} onChange={(v) => mudar('tipo', v as TipoDeMetodo)} options={TIPOS_DE_METODO.map((t) => ({ value: t.valor, label: t.rotulo }))} />
                </FormField>
                <FormField label="Conta de destino" htmlFor={idConta}>
                    <Select id={idConta} value={campos.contaId} onChange={(v) => mudar('contaId', v)} placeholder="Sem conta" options={contas.map((c) => ({ value: c.id, label: c.nome }))} />
                </FormField>
                <FormField label="Taxa" htmlFor={idTaxa} error={taxa === null || taxa >= 100 ? 'De 0 a 99,99' : undefined}>
                    <Input id={idTaxa} value={campos.taxa} onChange={(v) => mudar('taxa', v)} inputMode="decimal" suffix="%" invalid={taxa === null || taxa >= 100} />
                </FormField>
                <FormField label="Prazo de repasse" htmlFor={idPrazo} error={prazo === null ? 'Número de dias' : undefined}>
                    <Input id={idPrazo} value={campos.prazo} onChange={(v) => mudar('prazo', v)} inputMode="numeric" suffix="dias" invalid={prazo === null} />
                </FormField>
                <Switch checked={campos.ativo} onChange={(v) => mudar('ativo', v)} label="Forma ativa" style={{ gridColumn: '1 / -1' }} description="Inativa, some da baixa. Os pagamentos antigos continuam com ela." />
            </div>
        </Modal>
    );
}
